import { useEffect, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MapPin, Plus, Pencil, Trash2, X, User, Building2, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Gated from "@/components/Gated";
import MasterTable from "@/components/MasterTable";

const EMPTY = { name: "", type: "person", contact_person: "", phone: "", email: "", address: "", company_name: "", notes: "" };

export default function LocationsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/locations", { params: { q: q || undefined } }); setItems(data); }
    catch (e) { /* gated */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.patch(`/locations/${editId}`, form);
      else await api.post("/locations", form);
      toast.success("Saved"); setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openEdit = (l) => { setEditId(l.id); setForm({ ...EMPTY, ...l }); setOpen(true); };
  const remove = async (id) => { if (!window.confirm("Delete location?")) return; await api.delete(`/locations/${id}`); load(); toast.success("Deleted"); };

  return (
    <Gated feature="locations">
      <div className="p-8 space-y-6">
        <header className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="overline flex items-center gap-2"><MapPin size={12}/> Off-mailroom delivery points</div>
            <h1 className="text-4xl font-display mt-1">Locations.</h1>
            <p className="text-sm text-muted-foreground mt-1">Persons or companies at places without a mailroom. Used by the Drop workflow to route runners.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="loc-search" value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>{if(e.key==="Enter") load();}} placeholder="Search name / company…" className="pl-8 rounded-md w-56"/>
            </div>
            <Button data-testid="loc-new" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
              <Plus size={14} className="mr-2"/> Add location
            </Button>
          </div>
        </header>

        <MasterTable
          columns={[
            { key: "type", label: "Type", render: (l) => (
                l.type === "company"
                  ? <span className="text-[10px] uppercase tracking-widest font-bold border border-primary text-primary bg-red-50 px-1.5 py-0.5 rounded inline-flex items-center gap-1"><Building2 size={10}/> Company</span>
                  : <span className="text-[10px] uppercase tracking-widest font-bold border border-border text-muted-foreground px-1.5 py-0.5 rounded inline-flex items-center gap-1"><User size={10}/> Person</span>
            )},
            { key: "name", label: "Name", render: (l) => <span className="font-semibold">{l.name}</span> },
            { key: "contact_person", label: "Contact person", className: "text-xs",
              render: (l) => l.contact_person || "—" },
            { key: "phone", label: "Phone / Email", className: "mono text-xs", render: (l) => (
                <div>
                  <div>{l.phone || "—"}</div>
                  {l.email && <div>{l.email}</div>}
                </div>
            )},
            { key: "company_name", label: "Company", className: "text-xs",
              render: (l) => l.company_name || "—" },
            { key: "address", label: "Address", className: "text-xs max-w-xs truncate",
              render: (l) => <span title={l.address}>{l.address}</span> },
            { key: "actions", label: "", sortable: false, className: "text-right whitespace-nowrap", render: (l) => (
                <div className="inline-flex items-center gap-3">
                  <button data-testid={`loc-edit-${l.id}`} onClick={() => openEdit(l)} className="text-muted-foreground hover:text-primary"><Pencil size={13}/></button>
                  <button data-testid={`loc-del-${l.id}`} onClick={() => remove(l.id)} className="text-destructive hover:text-primary"><Trash2 size={13}/></button>
                </div>
            )},
          ]}
          rows={items}
          searchKeys={["name", "type", "contact_person", "phone", "email", "company_name", "address"]}
          getRowKey={(l) => l.id}
          testidPrefix="loc"
          defaultSort={{ key: "name", dir: "asc" }}
          emptyText="No locations match your filters."
        />

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="rounded-md max-w-lg" data-testid="loc-dialog">
            <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit location" : "Add location"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div>
                <span className="overline">Type *</span>
                <div className="mt-1 grid grid-cols-2 gap-3">
                  {[
                    { v: "person", label: "Person", Icon: User },
                    { v: "company", label: "Company", Icon: Building2 },
                  ].map(({ v, label, Icon }) => {
                    const active = form.type === v;
                    return (
                      <label key={v} data-testid={`loc-type-${v}`} className={`flex items-center gap-2 border rounded-md p-2 cursor-pointer ${active ? "border-primary bg-red-50" : "border-border"}`}>
                        <input type="radio" checked={active} onChange={() => setForm({...form, type: v})} className="accent-[#BE1E1E]"/>
                        <Icon size={14} className={active ? "text-primary" : "text-muted-foreground"}/>
                        <span className="text-sm font-semibold">{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <label className="block"><span className="overline">Name *</span>
                <Input data-testid="loc-name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required className="mt-1 rounded-md" placeholder={form.type === "company" ? "Company registered name" : "Person's full name"}/>
              </label>
              {form.type === "company" && (
                <label className="block"><span className="overline">Contact person *</span>
                  <Input data-testid="loc-contact" value={form.contact_person} onChange={e=>setForm({...form,contact_person:e.target.value})} required className="mt-1 rounded-md"/>
                </label>
              )}
              {form.type === "person" && (
                <label className="block"><span className="overline">Company / affiliation (optional)</span>
                  <Input data-testid="loc-company" value={form.company_name} onChange={e=>setForm({...form,company_name:e.target.value})} className="mt-1 rounded-md"/>
                </label>
              )}
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="overline">Phone</span>
                  <Input data-testid="loc-phone" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} className="mt-1 rounded-md font-mono"/>
                </label>
                <label className="block"><span className="overline">Email</span>
                  <Input data-testid="loc-email" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} className="mt-1 rounded-md"/>
                </label>
              </div>
              <label className="block"><span className="overline">Address *</span>
                <textarea data-testid="loc-address" value={form.address} onChange={e=>setForm({...form,address:e.target.value})} required rows={2} className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-sm"/>
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={()=>setOpen(false)} className="rounded-md"><X size={13} className="mr-1"/>Cancel</Button>
                <Button type="submit" data-testid="loc-submit" className="rounded-md bg-primary text-primary-foreground">Save</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Gated>
  );
}
